/**
 * Quiet Ask — browser-direct call to Gemini with the user's personal API key.
 *
 * This is the ONLY browser-direct LLM path in the app per ADR 0004.
 * Live hints, sentiment, and summary all go through Cloud Run + Vertex AI.
 *
 * The key lives in localStorage (apps/web/src/features/auth/store.ts).
 * Network requests go directly to generativelanguage.googleapis.com.
 *
 * Answers are concise light MARKDOWN (a bold lead line + dash bullets) and can be
 * streamed chunk-by-chunk so the rep sees them fill in live mid-call.
 */

import { type UserRole, isCERole } from "@scoach/types";
import type { Infographic, InfographicKind, TranscriptLine } from "@scoach/types";

import { effectiveGeminiKey, ensureGeminiKey } from "../../../../lib/geminiKey.ts";
import { useAuthStore } from "../../../auth/store.ts";

export interface QuietAskAnswer {
  q: string;
  /** Markdown answer: a bold one-line lead followed by dash bullets. */
  a: string;
  chips?: string[];
}

export const MODELS = ["gemini-3.8-flash", "gemini-3.7-flash"];

function endpoint(model: string) {
  return `https://generativelanguage.googleapis.com/v1beta/models/${model}:generateContent`;
}
function streamEndpoint(model: string) {
  return `https://generativelanguage.googleapis.com/v1beta/models/${model}:streamGenerateContent`;
}

const FORMAT_RULE = `The rep reads this LIVE mid-call, so it must be glanceable — output light MARKDOWN, never a paragraph:
- Line 1: a bold one-line punchline — **the single key takeaway** (≤8 words).
- Then 2-4 bullet lines, each starting with "- ", ONE short line each (≤14 words), leading with the concrete fact.
- No intro, no preamble, no closing sentence. Bullets only.`;

/**
 * Best-effort EXPLICIT target language from the SCRIPT of the question. Mirrors
 * the server's questionLanguageName (apps/api prompt-constants). "Same language
 * as the question" is unreliable when the call context is another language, so
 * non-Latin scripts are pinned explicitly; Latin scripts keep the generic phrase.
 */
function questionLanguageName(text: string): string {
  const t = text ?? "";
  if (/[֐-׿]/.test(t)) return "Hebrew (עברית)";
  if (/[؀-ۿ]/.test(t)) return "Arabic (العربية)";
  if (/[぀-ヿ]/.test(t)) return "Japanese (日本語)";
  if (/[가-힣]/.test(t)) return "Korean (한국어)";
  if (/[一-鿿]/.test(t)) return "Chinese (中文)";
  if (/[Ѐ-ӿ]/.test(t)) return "Russian (Русский)";
  if (/[ऀ-ॿ]/.test(t)) return "Hindi (हिन्दी)";
  if (/[ก-๛]/.test(t)) return "Thai (ไทย)";
  return "the same language as the question";
}

/** Force the answer language to follow the QUESTION, not the surrounding call. */
function answerLanguageDirective(languageName: string): string {
  return `\nLANGUAGE (critical): Write the ENTIRE answer in ${languageName}. Match the language of the QUESTION only — IGNORE the language of the surrounding conversation/transcript. An English question gets an English answer even in an otherwise Hebrew call, and vice-versa.`;
}

const CURRENCY_RULE = `Only reference CURRENT products/models — Google's current models are the Gemini 3.x family (e.g. Gemini 3.8 Flash, Gemini 3 Pro); never cite "Gemini 1.5", "Gemini 2.0", "PaLM" or "Bard" as current. State pricing in its correct unit (e.g. Provisioned Throughput is sold in throughput/GSU units over a commitment period, not per character); if unsure of a current figure, say so rather than guessing.`;

const SYSTEM = `You are SellSynq, a private, on-call assistant for an enterprise sales rep mid-customer-call.
${FORMAT_RULE}
${CURRENCY_RULE}`;

/**
 * Extra depth directive for technical sales roles (mirrors the server's
 * quickAskRoleDirective). "" for non-technical roles so their answers are unchanged.
 */
function ceDepthDirective(role: UserRole | undefined | null): string {
  if (!isCERole(role)) return "";
  return `\nROLE FOCUS: The rep is a technical solutions architect / sales engineer — a deeply technical audience. Answer at architecture depth: name the exact components/APIs/config, the relevant limits/quotas, integration and deployment specifics, and precise competitor mapping where relevant. Skip generic sales framing; assume the reader knows the basics and wants the concrete technical detail.`;
}

const URGENT_SYSTEM = `You are SellSynq, a real-time enterprise sales coach. The rep is mid-call and just hit "Urgent help" — they need an answer to the customer's last question NOW.
Find the most recent question (or implied question) the CLIENT asked; use the surrounding 5-10 lines of conversation as context. Prefer concrete facts (capabilities, integrations, pricing model). If the client mentioned a competitor or alternative, weave your company's superior advantage in.
${FORMAT_RULE}
${CURRENCY_RULE}`;

interface GeminiResponse {
  candidates?: Array<{ content?: { parts?: Array<{ text?: string }> } }>;
  error?: { message?: string };
}

export function textOf(json: GeminiResponse): string {
  const parts = json.candidates?.[0]?.content?.parts;
  return parts ? parts.map((p) => p.text ?? "").join("") : "";
}

/** True for "model not available" errors — worth trying the next model. */
function isModelMissing(msg: string): boolean {
  return (
    msg.includes("is not found") ||
    msg.includes("not supported") ||
    msg.includes("no longer available")
  );
}

export async function callGemini(key: string, body: object): Promise<GeminiResponse> {
  let lastErr: Error | null = null;
  for (const model of MODELS) {
    const res = await fetch(`${endpoint(model)}?key=${encodeURIComponent(key)}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    const json = (await res.json()) as GeminiResponse;
    if (res.ok) return json;
    const msg = json.error?.message ?? `Gemini ${res.status}`;
    if (isModelMissing(msg)) {
      lastErr = new Error(msg);
      continue;
    }
    throw new Error(msg);
  }
  throw lastErr ?? new Error("All Gemini models failed");
}

/**
 * Streaming call to Gemini (SSE). Invokes `onDelta` with each text chunk as it
 * arrives and resolves with the full accumulated text.
 */
async function streamGemini(
  key: string,
  body: object,
  onDelta: (text: string) => void,
): Promise<string> {
  let lastErr: Error | null = null;
  for (const model of MODELS) {
    const res = await fetch(`${streamEndpoint(model)}?alt=sse&key=${encodeURIComponent(key)}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    if (!res.ok || !res.body) {
      let msg = `Gemini ${res.status}`;
      try {
        const j = (await res.json()) as GeminiResponse;
        msg = j.error?.message ?? msg;
      } catch {
        // non-JSON error body
      }
      if (isModelMissing(msg)) {
        lastErr = new Error(msg);
        continue;
      }
      throw new Error(msg);
    }

    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buf = "";
    let full = "";
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      buf += decoder.decode(value, { stream: true });
      let sep = buf.indexOf("\n\n");
      while (sep !== -1) {
        const event = buf.slice(0, sep);
        buf = buf.slice(sep + 2);
        sep = buf.indexOf("\n\n"); // advance now so `continue` can't loop forever
        const dataLine = event.split("\n").find((l) => l.startsWith("data:"));
        if (!dataLine) continue;
        const data = dataLine.slice(5).trim();
        if (!data || data === "[DONE]") continue;
        try {
          const delta = textOf(JSON.parse(data) as GeminiResponse);
          if (delta) {
            full += delta;
            onDelta(delta);
          }
        } catch {
          // ignore a malformed SSE line
        }
      }
    }
    return full;
  }
  throw lastErr ?? new Error("All Gemini models failed");
}

export type AskDepth = "short" | "deep";

/** Deep-answer override for the browser-direct fallback (mirrors the server's
 * QUIET_DEEP_DIRECTIVE). "" for short (default) keeps the glanceable format. */
function depthDirective(depth: AskDepth | undefined): string {
  if (depth !== "deep") return "";
  return `\nDEPTH OVERRIDE (the rep asked for a DEEP, detailed answer — this SUPERSEDES the brevity limits above): give a thorough, well-structured light-markdown answer (bold lead, then as much useful detail as the question warrants; brief **bold sub-labels** ok, no # headings/tables/code fences). Be concrete: exact products/APIs/config, limits/quotas, trade-offs, and a short example or steps. Depth over brevity, but skimmable.`;
}

function askBody(question: string, role?: UserRole | null, depth?: AskDepth) {
  return {
    systemInstruction: {
      role: "system",
      parts: [
        {
          text:
            SYSTEM +
            ceDepthDirective(role) +
            depthDirective(depth) +
            answerLanguageDirective(questionLanguageName(question)),
        },
      ],
    },
    contents: [{ role: "user", parts: [{ text: question }] }],
    generationConfig: { temperature: 0.4, maxOutputTokens: depth === "deep" ? 2048 : 1024 },
  };
}

export async function quietAsk(
  question: string,
  role?: UserRole | null,
  depth?: AskDepth,
): Promise<QuietAskAnswer> {
  const key = effectiveGeminiKey();
  if (!key) throw new Error("No Gemini API key set. Add one in Settings.");
  const json = await callGemini(key, askBody(question, role, depth));
  return { q: question, a: textOf(json).trim() };
}

/** Streaming Quiet Ask — emits text deltas via `onDelta`, resolves with full text. */
export async function streamQuietAsk(
  question: string,
  onDelta: (text: string) => void,
  role?: UserRole | null,
  depth?: AskDepth,
): Promise<QuietAskAnswer> {
  let key = effectiveGeminiKey();
  if (!key) {
    key = await ensureGeminiKey(true);
  }
  if (!key) throw new Error("No Gemini API key set. Add one in Settings.");
  try {
    const a = await streamGemini(key, askBody(question, role, depth), onDelta);
    return { q: question, a: a.trim() };
  } catch (err: unknown) {
    const msg = (err as Error)?.message || "";
    if (msg.includes("401") || msg.includes("API key not valid") || msg.includes("API_KEY_INVALID")) {
      useAuthStore.getState().setGeminiKey(null);
      const freshKey = await ensureGeminiKey(true);
      if (freshKey && freshKey !== key) {
        const a = await streamGemini(freshKey, askBody(question, role, depth), onDelta);
        return { q: question, a: a.trim() };
      }
    }
    throw err;
  }
}

/**
 * Build the Urgent-help request body + the question to display. Uses the last 12
 * transcript lines as context; the model finds the client's most recent question
 * itself (Hebrew questions don't always end with "?").
 */
function urgentBody(transcript: TranscriptLine[]): { body: object; displayedQ: string } {
  if (transcript.length === 0) {
    throw new Error("No conversation yet — start the meeting first.");
  }
  const recent = transcript.slice(-12);
  const context = recent.map((l) => `[${l.t}] ${l.name} (${l.lang}): ${l.text}`).join("\n");
  const lastClient = [...recent].reverse().find((l) => l.speaker === "client");
  const displayedQ = (lastClient ?? recent[recent.length - 1])?.text ?? "";

  const body = {
    systemInstruction: {
      role: "system",
      // Answer in the language the CLIENT used for their question, not whatever
      // language dominates the rest of the transcript.
      parts: [{ text: URGENT_SYSTEM + answerLanguageDirective(questionLanguageName(displayedQ)) }],
    },
    contents: [
      {
        role: "user",
        parts: [
          {
            text: `Recent transcript:\n${context}\n\nProduce the answer to the client's most recent question (or the most pressing topic they raised) right now.`,
          },
        ],
      },
    ],
    generationConfig: { temperature: 0.3, maxOutputTokens: 1024 },
  };
  return { body, displayedQ };
}

/**
 * Urgent help — uses the rolling transcript context (especially the client's most
 * recent question) to generate a fast answer the rep can use mid-call. No typing
 * required — one click pulls everything from what's already on the transcript.
 */
export async function urgentHelp(transcript: TranscriptLine[]): Promise<QuietAskAnswer> {
  const key = effectiveGeminiKey();
  if (!key) throw new Error("No Gemini API key set. Add one in Settings.");
  const { body, displayedQ } = urgentBody(transcript);
  const json = await callGemini(key, body);
  return { q: displayedQ, a: textOf(json).trim() };
}

/** Streaming Urgent help — emits text deltas via `onDelta`, resolves with full text. */
export async function streamUrgentHelp(
  transcript: TranscriptLine[],
  onDelta: (text: string) => void,
): Promise<QuietAskAnswer> {
  const key = effectiveGeminiKey();
  if (!key) throw new Error("No Gemini API key set. Add one in Settings.");
  const { body, displayedQ } = urgentBody(transcript);
  const a = await streamGemini(key, body, onDelta);
  return { q: displayedQ, a: a.trim() };
}

const INFOGRAPHIC_SYSTEM = `You are a real-time Mermaid chart generator for a live sales call. ALWAYS produce a chart — never skip.

Pick the best Mermaid diagram type:
- flowchart TD: architecture, processes, decision trees (use most often)
- graph LR: horizontal flows, pipelines, migration paths
- pie: cost breakdowns, usage distribution
- sequenceDiagram: interactions between systems

Return STRICT JSON:
{ "kind": "flow", "title": "<short title>", "mermaid": "<valid mermaid syntax>" }

Keep charts simple: 4-8 nodes, short labels (max 20 chars). Match conversation language.`;

export async function generateClientInfographic(
  transcript: TranscriptLine[],
): Promise<Infographic | null> {
  const key = effectiveGeminiKey();
  if (!key || transcript.length < 3) return null;

  const recent = transcript.slice(-15);
  const context = recent.map((l) => `[${l.t}] ${l.name}: ${l.text}`).join("\n");

  const body = {
    systemInstruction: { role: "system", parts: [{ text: INFOGRAPHIC_SYSTEM }] },
    contents: [
      {
        role: "user",
        parts: [
          {
            text: `Recent transcript:\n${context}\n\nGenerate a Mermaid chart for this conversation.`,
          },
        ],
      },
    ],
    generationConfig: {
      temperature: 0.4,
      maxOutputTokens: 1024,
      responseMimeType: "application/json",
    },
  };

  try {
    const json = await callGemini(key, body);
    const raw = json.candidates?.[0]?.content?.parts?.[0]?.text ?? "";
    const cleaned = raw.replace(/^```json\s*|\s*```$/g, "").trim();
    const parsed = JSON.parse(cleaned || "{}") as {
      kind?: string;
      title?: string;
      mermaid?: string;
    };
    if (!parsed.title || !parsed.mermaid) return null;
    return {
      id: crypto.randomUUID(),
      kind: (parsed.kind as InfographicKind) || "flow",
      title: parsed.title,
      data: { nodes: [], edges: [] },
      mermaid: parsed.mermaid,
      generatedAt: new Date().toISOString(),
    };
  } catch {
    return null;
  }
}
